import { type FC, type ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DashboardKpiCard } from "src/components/common";
import {
  KPI_PAGE_SIZE,
  type EngagementKpiMetric,
} from "../../data/engagementOverviewTypes";
import EngagementEmptyState from "./EngagementEmptyState";
import EngagementKpiCarousel from "./EngagementKpiCarousel";
import { getEngagementTooltipInfo } from "./getEngagementTooltipInfo";

export interface EngagementKpiGridProps {
  metrics: EngagementKpiMetric[];
  iconsById: Record<string, ReactNode>;
  tooltipKeyById?: Record<string, string>;
  /** Section heading shown when showSectionHeading is true. */
  sectionTitle?: string;
  showSectionHeading?: boolean;
  showPagination?: boolean;
  pageSize?: number;
  /** Static label, or per-metric resolver. */
  comparisonLabel?: string | ((_metricId: string) => string);
  emptyMessage?: string;
  ariaLabel?: string;
  paginationAriaLabel?: string;
}

const EngagementKpiGrid: FC<EngagementKpiGridProps> = ({
  metrics,
  iconsById,
  tooltipKeyById = {},
  sectionTitle,
  showSectionHeading = false,
  showPagination = false,
  pageSize = KPI_PAGE_SIZE,
  comparisonLabel,
  emptyMessage,
  ariaLabel,
  paginationAriaLabel,
}) => {
  const { t } = useTranslation();
  const resolvedComparisonLabel = comparisonLabel ?? t("engagementOverview.shared.comparisonLabel");
  const resolvedEmptyMessage = emptyMessage ?? t("engagementOverview.shared.kpiEmptyMessage");
  const resolvedAriaLabel = ariaLabel ?? t("engagementOverview.shared.kpiAriaLabel");
  const resolvedPaginationAriaLabel =
    paginationAriaLabel ?? t("engagementOverview.shared.kpiPaginationAriaLabel");

  const cards = useMemo(() => {
    const resolveLabel = (metricId: string) =>
      typeof resolvedComparisonLabel === "function"
        ? resolvedComparisonLabel(metricId)
        : resolvedComparisonLabel;

    return (metrics ?? []).map(metric => (
      <DashboardKpiCard
        key={metric.id}
        className="engagement-overview-kpi-card"
        title={metric.title}
        value={metric.value}
        icon={iconsById?.[metric.id]}
        changePercent={metric.changePercent}
        tooltip={getEngagementTooltipInfo(t, tooltipKeyById?.[metric.id] ?? metric.id)}
        uppercaseTitle
        comparisonLabel={resolveLabel(metric.id)}
      />
    ));
  }, [metrics, iconsById, tooltipKeyById, resolvedComparisonLabel, t]);

  if (!metrics?.length) {
    return (
      <section className="engagement-overview-kpi-section" aria-label={resolvedAriaLabel}>
        {showSectionHeading && sectionTitle ? (
          <div className="engagement-overview-kpi-section__header">
            <h2>{sectionTitle}</h2>
          </div>
        ) : null}
        <EngagementEmptyState message={resolvedEmptyMessage} />
      </section>
    );
  }

  return (
    <section className="engagement-overview-kpi-section" aria-label={resolvedAriaLabel}>
      {showSectionHeading && sectionTitle ? (
        <div className="engagement-overview-kpi-section__header">
          <h2>{sectionTitle}</h2>
        </div>
      ) : null}
      {showPagination ? (
        <EngagementKpiCarousel
          items={cards}
          pageSize={pageSize}
          ariaLabel={resolvedPaginationAriaLabel}
          emptyState={<EngagementEmptyState message={resolvedEmptyMessage} />}
        />
      ) : (
        <div className="engagement-overview-kpi-carousel">
          <div className="engagement-overview-kpi-carousel__grid">{cards}</div>
        </div>
      )}
    </section>
  );
};

export default EngagementKpiGrid;
